import { DealType } from './SharedEnums';
import FIMenuPaymentAmount from './FIMenuPaymentAmount';
import financials from '@core/services/financials';
import util from '@core/services/util';

export interface IFIMenuLeaseTerm {
	buyrate?: number;
	buyRateMoneyFactor?: number;
	finalNumbersPerCoverageTerm?: any[];
	moneyfactor?: number;
	paymentAmounts?: FIMenuPaymentAmount[];
	residual?: number;
	residualAmount?: number;
	sellrate?: number;
	show?: boolean;
	term?: number;
}

export default class FIMenuLeaseTerm implements IFIMenuLeaseTerm {
	buyrate?: number;
	buyRateMoneyFactor?: number;
	finalNumbersPerCoverageTerm?: any[];
	moneyfactor?: number;
	paymentAmounts?: FIMenuPaymentAmount[];
	residual?: number;
	residualAmount?: number;
	sellrate?: number;
	show?: boolean;
	term?: number;

	constructor(init?: IFIMenuLeaseTerm) {
		if (init) {
			this.term = init.term;
			this.residual = init.residual;
			this.moneyfactor = init.moneyfactor;
			this.buyrate = init.buyrate;
			this.show = init.show;
			this.paymentAmounts = init.paymentAmounts?.map(cttf => new FIMenuPaymentAmount(cttf)) ?? [];

			//front end properties only
			this.residualAmount = init.residualAmount ?? null;
			this.sellrate = (init.sellrate ?? init?.moneyfactor) ? init.moneyfactor * 2400 : null;
			this.buyRateMoneyFactor = (init.buyRateMoneyFactor ?? init.buyrate) ? init.buyrate / 2400 : null;
			this.finalNumbersPerCoverageTerm = [];
		}
	}

	isAvailable() {
		return this.term && this.show && util.hasValue(this.residual) && util.hasValue(this.moneyfactor);
	}

	paymentCalculationWithoutCoverage(dealType: DealType, otd: number, msrp: number, stateTaxRate: number, countyTaxRate: number, state: string, lenderCode: string) {
		//params
		//dealType - should always be 'lease'
		//otd - should be the Out the Door price
		//msrp - MSRP which is what is used to calculate residual amount
		//stateTaxRate - only include this if the payment gets taxed. this should be the state's tax rate (represented as the full number, in other words, 6.5% would be 6.5, not .065)
		//countyTaxRate - only include this if the payment gets taxed. this should be the county's tax rate (represented as the full number, in other words, 1.0% would be 1.0, not .01)
		//state - only need to include this if the payment gets taxed. gets passed in to the sales tax calculations
		if (dealType.toLowerCase() === 'lease' && this.term) {
			if (stateTaxRate) {
				let baseLeasePayment = this.baseCalculateLeasePayment(this.term, otd, this.residual, this.moneyfactor, msrp, lenderCode, this.sellrate);
				let stateTax = financials.calculateStateSalesTax(baseLeasePayment, stateTaxRate, state);
				let countyTax = financials.calculateCountySalesTax(baseLeasePayment, countyTaxRate, state);
				//console.log('baseLeasePayment', baseLeasePayment);
				//console.log('stateTaxRate', stateTaxRate);
				//console.log('countyTaxRate', countyTaxRate);
				//console.log('state', state);
				//console.log('stateTax', stateTax);
				//console.log('countyTax', countyTax);
				return financials.roundFloatNumberWithoutTrailingZeroes(Number(baseLeasePayment) + Number(stateTax) + Number(countyTax), 2);
			} else {
				return this.baseCalculateLeasePayment(this.term, otd, this.residual, this.moneyfactor, msrp, lenderCode, this.sellrate);
			}
		} else {
			return null;
		}
	}

	paymentCalculation(dealType: DealType, otd: number, msrp: number, stateTaxRate: number, countyTaxRate: number, state?: string, lenderCode?: string) {
		//params
		//dealType - should always be 'lease'
		//otd - should be the Out the Door price
		//coverage - should be the coverage total - depending on what state you're in, it needs to not include tax, since we're taxing the monthly payment
		//msrp - MSRP which is what is used to calculate residual amount
		//stateTaxRate - only include this if the payment gets taxed. this should be the state's tax rate (represented as the full number, in other words, 6.5% would be 6.5, not .065)
		//countyTaxRate - only include this if the payment gets taxed. this should be the county's tax rate (represented as the full number, in other words, 1.0% would be 1.0, not .01)
		//state - only need to include this if the payment gets taxed. gets passed in to the sales tax calculations
		if (dealType.toLowerCase() === 'lease' && this.term)
			if (stateTaxRate) {
				let baseLeasePayment = this.baseCalculateLeasePayment(this.term, otd, this.residual, this.moneyfactor, msrp, lenderCode, this.sellrate);
				let stateTax = financials.calculateStateSalesTax(baseLeasePayment, stateTaxRate, state);
				let countyTax = financials.calculateCountySalesTax(baseLeasePayment, countyTaxRate, state);

				let buyrateBaseLeasePayment = this.baseCalculateLeasePayment(this.term, otd, this.residual, this.buyRateMoneyFactor, msrp, lenderCode, this.sellrate);
				let stateTaxAtBuyRate = financials.calculateStateSalesTax(buyrateBaseLeasePayment, stateTaxRate, state);
				let countyTaxAtBuyRate = financials.calculateCountySalesTax(buyrateBaseLeasePayment, countyTaxRate, state);
				return {
					payment: financials.roundFloatNumberWithoutTrailingZeroes(Number(baseLeasePayment) + Number(stateTax) + Number(countyTax), 2),
					paymentAtBuyRate: financials.roundFloatNumberWithoutTrailingZeroes(
						Number(buyrateBaseLeasePayment) + Number(stateTaxAtBuyRate) + Number(countyTaxAtBuyRate),
						2,
					),
				};
			} else {
				return {
					payment: this.baseCalculateLeasePayment(this.term, otd, this.residual, this.moneyfactor, msrp, lenderCode, this.sellrate),
					paymentAtBuyRate: this.baseCalculateLeasePayment(this.term, otd, this.residual, this.buyRateMoneyFactor, msrp, lenderCode, this.sellrate),
				};
			}
		else
			return {
				payment: null,
				paymentAtBuyRate: null,
			};
	}

	baseCalculateLeasePayment(term: number, adjustedCapCost: number, residual: number, moneyFactor: number, vehicleValue: number, lenderCode: string, sellrate: number) {
		switch (lenderCode) {
			case 'ALLY':
				return financials.calculateLeastPaymentAlly(term, adjustedCapCost, residual, sellrate);
			default:
				return financials.calculateLeasePayment(term, adjustedCapCost, residual, moneyFactor, vehicleValue);
		}
	}
}
